import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Shield, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';

const CRITICAL_DAYS = 90;
const WARN_DAYS = 365;

function daysTo(d) { if (!d) return null; const dt = new Date(d); if (isNaN(dt)) return null; return Math.round((dt - new Date()) / 86400000); }

/**
 * Compact IP Renewal summary card for the CEO dashboard.
 *
 * Shows total marks + count of expired / critical (< 90d) / warn (< 1y).
 * Renders a green "All marks current" pill when nothing needs attention,
 * otherwise surfaces the top 3 most-urgent marks and links to /ip-register.
 *
 * CEO-only (API is gated server-side).
 */
export default function IPRenewalSummary() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/ip-register');
        setItems(Array.isArray(r.data) ? r.data : []);
      } catch (_) { setItems(null); } // non-CEO → 403, silent
    })();
  }, []);

  if (items == null) return null;
  if (items.length === 0) return null;

  // Only track marks that could actually lapse — i.e. currently active registrations.
  // Removed / Abandoned / Withdrawn / Refused marks don't need renewal; showing them
  // as "6058 days overdue" pollutes the CEO alert panel.
  const LIVE_STATUSES = new Set(['Registered', 'Pending', 'Opposed', 'Objected', 'Accepted']);
  const live = items.filter(i => LIVE_STATUSES.has(i.registration_status || i.status));

  const enriched = live.map(i => ({ ...i, days: daysTo(i.expiry_date) }));
  const attention = enriched
    .filter(i => i.days != null && i.days <= WARN_DAYS)
    .sort((a, b) => a.days - b.days);
  const expired = attention.filter(i => i.days < 0).length;
  const critical = attention.filter(i => i.days >= 0 && i.days <= CRITICAL_DAYS).length;
  const warn = attention.filter(i => i.days > CRITICAL_DAYS && i.days <= WARN_DAYS).length;

  const nothingUrgent = attention.length === 0;

  return (
    <div
      className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow"
      style={{ borderColor: '#E5E1DC' }}
      onClick={() => navigate('/ip-register')}
      data-testid="ip-renewal-summary"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: nothingUrgent ? '#DCFCE7' : '#FEE2E2' }}>
            {nothingUrgent
              ? <CheckCircle2 size={14} style={{ color: '#16A34A' }} />
              : <AlertTriangle size={14} style={{ color: '#DC2626' }} />}
          </div>
          <div>
            <p className="text-sm font-semibold flex items-center gap-1" style={{ color: '#1B2A4A' }}>
              <Shield size={12} style={{ color: '#6B7280' }} /> IP Renewals
            </p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {live.length} active mark{live.length === 1 ? '' : 's'} tracked
              {nothingUrgent && ' · all current'}
            </p>
          </div>
        </div>
        <ChevronRight size={14} style={{ color: '#9CA3AF' }} />
      </div>

      {!nothingUrgent && (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ip-renewal-counts">
            {expired > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>{expired} expired</span>}
            {critical > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>{critical} &lt; {CRITICAL_DAYS}d</span>}
            {warn > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>{warn} &lt; 1yr</span>}
          </div>
          <div className="space-y-1">
            {attention.slice(0, 3).map((i) => (
              <div key={i.id} className="flex items-center justify-between text-[11px]" data-testid={`ip-renewal-row-${i.id}`}>
                <span className="truncate" style={{ color: '#1B2A4A' }}>
                  {i.mark_name} <span style={{ color: '#9CA3AF' }}>· {i.class_info || '—'}</span>
                </span>
                <span className="font-semibold tabular-nums" style={{ color: i.days < 0 ? '#991B1B' : i.days <= CRITICAL_DAYS ? '#991B1B' : '#92400E' }}>
                  {i.days < 0 ? `${Math.abs(i.days)}d overdue` : `${i.days}d`}
                </span>
              </div>
            ))}
            {attention.length > 3 && <p className="text-[10px] text-center" style={{ color: '#6B7280' }}>+{attention.length - 3} more →</p>}
          </div>
        </>
      )}
    </div>
  );
}
